import React, { useState, useEffect } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSafeNavigate, useUnsavedChangesContext } from '../hooks/useUnsavedChangesWarning';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { auteursAPI } from '../services/api';

function ModifierAuteurPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const { allowNextNavigation } = useUnsavedChangesContext();

  // Détecter si c'est une création ou une modification
  const isCreating = !id || id === 'ajouter';

  // État pour détecter les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { control, handleSubmit, formState: { errors, isDirty }, reset } = useForm({
    defaultValues: {
      prenom: '',
      nom: '',
      date_naissance: '',
      date_deces: '',
      biographie: ''
    }
  });

  // Récupérer les données de l'auteur (seulement en mode modification)
  const { data: auteur, isLoading, error } = useQuery(
    ['auteur', id],
    () => auteursAPI.getById(id).then(res => res.data.data),
    {
      enabled: !isCreating && !!id
    }
  );

  // Surveiller les modifications du formulaire
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  // Pré-remplir le formulaire quand les données sont disponibles (mode modification)
  useEffect(() => {
    if (auteur && !isCreating) {
      reset({
        prenom: auteur.prenom || '',
        nom: auteur.nom || '',
        date_naissance: auteur.date_naissance ? auteur.date_naissance.split('T')[0] : '',
        date_deces: auteur.date_deces ? auteur.date_deces.split('T')[0] : '',
        biographie: auteur.biographie || ''
      });
    }
  }, [auteur, reset, isCreating]);

  // Mutation pour créer ou mettre à jour l'auteur
  const saveAuteurMutation = useMutation(
    (data) => {
      // Nettoyer les champs vides
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== '')
      );
      return isCreating 
        ? auteursAPI.create(cleanData)
        : auteursAPI.update(id, cleanData);
    },
    {
      onSuccess: (response) => {
        // Réinitialiser les modifications non sauvegardées et autoriser la navigation
        setHasUnsavedChanges(false);
        allowNextNavigation();
        
        if (isCreating) {
          toast.success(t('ajouterAuteurPage.createSuccess'));
          queryClient.invalidateQueries('auteurs');
          navigate(`/auteurs/${response.data.data.id}`);
        } else {
          toast.success(t('modifierAuteurPage.updateSuccess'));
          queryClient.invalidateQueries(['auteur', id]);
          queryClient.invalidateQueries('auteurs');
          navigate(`/auteurs/${id}`);
        }
      },
      onError: (error) => {
        console.error(`Erreur lors de la ${isCreating ? 'création' : 'modification'}:`, error);
        
        if (error.response?.status === 409) {
          toast.error(isCreating ? t('ajouterAuteurPage.alreadyExists') : t('modifierAuteurPage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(isCreating ? t('ajouterAuteurPage.createError') : t('modifierAuteurPage.updateError'));
        }
      }
    }
  );

  const onSubmit = (data) => {
    saveAuteurMutation.mutate(data);
  };

  const handleBack = () => {
    if (isCreating) {
      navigate('/auteurs');
    } else {
      navigate(`/auteurs/${id}`);
    }
  };

  // Afficher le chargement seulement en mode modification
  if (!isCreating && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Afficher l'erreur seulement en mode modification
  if (!isCreating && error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('modifierAuteurPage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  // Afficher "non trouvé" seulement en mode modification
  if (!isCreating && !auteur) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('modifierAuteurPage.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header avec navigation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 500,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {isCreating ? t('ajouterAuteurPage.title') : t('modifierAuteurPage.title')}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              startIcon={saveAuteurMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saveAuteurMutation.isLoading}
              sx={{ 
                ml: 2,
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
              }}
            >
              {saveAuteurMutation.isLoading 
                ? (isCreating ? t('ajouterAuteurPage.saving') : t('modifierAuteurPage.saving'))
                : (isCreating ? t('ajouterAuteurPage.save') : t('modifierAuteurPage.save'))}
            </Button>
          </Box>
        </Box>

        {/* Formulaire */}
        <Controller
          name="prenom"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={isCreating ? t('ajouterAuteurPage.firstName') : t('modifierAuteurPage.firstName')}
              margin="normal"
              disabled={saveAuteurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />

        <Controller
          name="nom"
          control={control}
          rules={{ 
            required: isCreating ? t('ajouterAuteurPage.lastNameRequired') : t('modifierAuteurPage.lastNameRequired'),
            minLength: { value: 1, message: isCreating ? t('ajouterAuteurPage.lastNameMinLength') : t('modifierAuteurPage.lastNameMinLength') }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={isCreating ? t('ajouterAuteurPage.lastName') : t('modifierAuteurPage.lastName')}
              margin="normal"
              error={!!errors.nom}
              helperText={errors.nom?.message}
              disabled={saveAuteurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />

        <Controller
          name="date_naissance"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={isCreating ? t('ajouterAuteurPage.birthDate') : t('modifierAuteurPage.birthDate')}
              type="date"
              margin="normal"
              InputLabelProps={{ shrink: true }}
              disabled={saveAuteurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />

        <Controller
          name="date_deces"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={isCreating ? t('ajouterAuteurPage.deathDate') : t('modifierAuteurPage.deathDate')}
              type="date"
              margin="normal"
              InputLabelProps={{ shrink: true }}
              disabled={saveAuteurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />

        <Controller
          name="biographie"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={isCreating ? t('ajouterAuteurPage.biography') : t('modifierAuteurPage.biography')}
              multiline
              rows={4}
              margin="normal"
              disabled={saveAuteurMutation.isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
        />
      </form>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={isCreating ? t('ajouterAuteurPage.unsavedChangesMessage') : t('modifierAuteurPage.unsavedChangesMessage')}
      />
    </Box>
  );
}

export default ModifierAuteurPage;

